import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import TextField from '@material-ui/core/TextField';
import { Formik } from 'formik';
import Immutable from 'immutable';
import * as FormUtils from 'global/utils/form-utils';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Fab from '@material-ui/core/Fab';
import axios from 'global/axios-instance';
import _ from 'lodash';
import * as RouterActions from 'connected-react-router';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as GlobalActions from '../../global/actions/global-actions';
import Paper from '@material-ui/core/Paper';
import LogoutIcon from '@material-ui/icons/ExitToApp';
import Autocomplete from '@material-ui/lab/Autocomplete';
import * as Yup from 'yup';

const useStyles = makeStyles(theme => ({
    container: {
        maxWidth: '1200px',
        width: '100%',
        margin: '1rem auto',
        padding: '2rem',
        textAlign: 'center',
        position: 'relative'
    },
    formContainer: {
        display: 'flex',
        flexWrap: 'wrap'
    },
    formControl: {
        margin: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            width: '30%'
        }
    },
    buttonContainer: {
        textAlign: 'right',
        margin: '1rem',
        '& > :last-child': {
            marginLeft: '.5rem'
        }
    },
    logoutButton: {
        position: 'absolute',
        top: '1rem',
        right: '1rem'
    }
}));

const QUESTIONNAIRE_FIELDS = [
    'userId',
    'employedCountry',
    'residenceCountry',
    'accessCountry'
];

const LoginCountryQuestionnaire = ({ push, updateSession, logout, currentUser }) => {
    const [countries, setCountries] = useState(Immutable.List());

    const classes = useStyles();
    const form = useRef();

    const schema = Yup.object().shape({
        employedCountry: Yup.string().required('Please select a country'),
        residenceCountry: Yup.string().required('Please select a country'),
        accessCountry: Yup.string().required('Please select a country')
    });

    useEffect(() => {
        const load = async () => {
            const countryResponse = await axios.get('/lookups/countries');

            setCountries(Immutable.fromJS(countryResponse.data));
        };

        load();
    }, []);

    const back = () => {
        push('/questionnaire-landing');
    };

    const onSubmit = async (values) => {
        const payload = FormUtils.cleanupEmptyStrings(_.pick(values, QUESTIONNAIRE_FIELDS));
        const result = await axios.post('/user-creation-requests', payload);
        const { user } = result.data;

        await updateSession({ user });

        return push('/access-pending');
    };

    const onLogout = () => {
        logout();
        window.location.href = '/';
    };

    let initialValues = FormUtils.getInitialValues(QUESTIONNAIRE_FIELDS);
    initialValues.userId = currentUser.userId;

    return (<Paper className={classes.container}>
        <Fab size='medium' variant='extended' className={classes.logoutButton} onClick={onLogout}><LogoutIcon/>Logout</Fab>
        <h2>Please answer these questions to continue logging in.</h2>
        <Formik
            ref={form}
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={(values, { setSubmitting }) => {
                onSubmit(values)
                    .then(() => setSubmitting(false));
            }}
        >
            {({
                values,
                errors,
                touched,
                handleBlur,
                handleSubmit,
                setFieldTouched,
                setFieldValue,
                isSubmitting
                /* and other goodies */
            }) => (
                <form onSubmit={handleSubmit}>
                    <div className={classes.formContainer}>
                        <input type='hidden' name='userId' value={values.userId} />
                        <FormControl component='fieldset' className={classes.formControl}>
                            <FormLabel component='legend'>What country are you employed in?</FormLabel>
                            <Autocomplete
                                name='employedCountry'
                                options={countries.toArray()}
                                getOptionLabel={(option) => _.isEmpty(option) ? '' : option.get('countryName')}
                                renderInput={(params) => <TextField {...params} className={classes.textField} label='Country'
                                    error={errors.employedCountry && touched.employedCountry}
                                />}
                                value={_.isEmpty(values.employedCountry) ? '' : countries.find(c => c.get('countryCode') === values.employedCountry)}
                                onBlur={handleBlur}
                                onChange={(ev, v) => {
                                    setFieldValue('employedCountry', (v && v.get('countryCode')) || '');
                                }}
                            />

                            {errors.employedCountry && touched.employedCountry &&
                                <FormHelperText error>{errors.employedCountry}</FormHelperText>
                            }
                        </FormControl>
                        <FormControl component='fieldset' className={classes.formControl}>
                            <FormLabel component='legend'>What country do you live in?</FormLabel>
                            <Autocomplete
                                name='residenceCountry'
                                options={countries.toArray()}
                                getOptionLabel={(option) => _.isEmpty(option) ? '' : option.get('countryName')}
                                renderInput={(params) => <TextField {...params} className={classes.textField} label='Country'
                                    error={errors.residenceCountry && touched.residenceCountry}
                                />}
                                value={_.isEmpty(values.residenceCountry) ? '' : countries.find(c => c.get('countryCode') === values.residenceCountry)}
                                onBlur={handleBlur}
                                onChange={(ev, v) => {
                                    setFieldValue('residenceCountry', (v && v.get('countryCode')) || '');
                                }}
                            />

                            {errors.residenceCountry && touched.residenceCountry &&
                                <FormHelperText error>{errors.residenceCountry}</FormHelperText>
                            }
                        </FormControl>
                        <FormControl component='fieldset' className={classes.formControl}>
                            <FormLabel component='legend'>What country do you need access to?</FormLabel>
                            <Autocomplete
                                name='accessCountry'
                                options={countries.toArray()}
                                getOptionLabel={(option) => _.isEmpty(option) ? '' : option.get('countryName')}
                                renderInput={(params) => <TextField {...params} className={classes.textField} label='Country'
                                    error={errors.accessCountry && touched.accessCountry}
                                />}
                                value={_.isEmpty(values.accessCountry) ? '' : countries.find(c => c.get('countryCode') === values.accessCountry)}
                                onBlur={handleBlur}
                                onChange={(ev, v) => {
                                    setFieldValue('accessCountry', (v && v.get('countryCode')) || '');
                                }}
                            />

                            {errors.accessCountry && touched.accessCountry &&
                                <FormHelperText error>{errors.accessCountry}</FormHelperText>
                            }
                        </FormControl>
                    </div>
                    <div className={classes.buttonContainer} >
                        <Fab size='medium' variant='extended' onClick={back} disabled={isSubmitting} className={classes.button}>
                            <ArrowBackIcon />
                                    Back
                        </Fab>
                        <Fab color='primary' size='medium' variant='extended' onClick={handleSubmit} disabled={isSubmitting} className={classes.button}>
                            <ArrowUpwardIcon />
                                    Submit
                        </Fab>
                    </div>
                </form>
            )}
        </Formik>
    </Paper>
    );
};

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators({
        push: RouterActions.push,
        logout: GlobalActions.logout,
        updateSession: GlobalActions.updateSession
    }, dispatch);
};

const mapStateToProps = (state) => {
    return {
        currentUser: state.session.user
    };
};

LoginCountryQuestionnaire.propTypes = {
    push: PropTypes.func.isRequired,
    updateSession: PropTypes.func.isRequired,
    logout: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginCountryQuestionnaire);
